import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, ArrowRight, CalendarClock, Truck, CheckCircle2, ClipboardList } from "lucide-react";
import { useMoveJobs } from "../../data/truckingData";
import NewMoveJobDialog from "../../components/trucking/NewMoveJobDialog";
import { formatCalendarDate } from "../../lib/calendar-date";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { unwrapArray } from "../../lib/api-envelope";
import { displayValue } from "../../features/fieldiq-handoff/contracts";

/**
 * A move job as GET /trucking/move-jobs sends it: `mj.*` plus `operator_name`,
 * `coordinator_name`, the master BOL's `master_bol_number`, `master_bol_status`,
 * `total_loads` and `loads_signed`, and `bol_count`.
 *
 * The shared `MoveJob` type names `operator` and `signed_loads`, which the API
 * has never sent, so the Operator column was always a dash and the progress bar
 * read "/ signed" at 0%. `total_loads` and `loads_signed` are the MASTER BOL's
 * counts and are null until one exists; `bol_count` is how many loads are
 * linked. Two different facts, shown as two different things.
 */
interface MoveJobRow {
  id: string;
  name: string;
  operator_name: string | null;
  rig_number: string | null;
  origin_lsd: string | null;
  destination_lsd: string | null;
  planned_start: string | null;
  status: string | null;
  master_bol_number: string | null;
  total_loads: number | null;
  loads_signed: number | null;
  bol_count: number | null;
}

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));
const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function toRow(r: Record<string, unknown>): MoveJobRow {
  return {
    id: String(r.id ?? ""),
    name: String(r.name ?? "—"),
    operator_name: str(r.operator_name),
    rig_number: str(r.rig_number),
    origin_lsd: str(r.origin_lsd),
    destination_lsd: str(r.destination_lsd),
    planned_start: str(r.planned_start),
    status: str(r.status),
    master_bol_number: str(r.master_bol_number),
    total_loads: num(r.total_loads),
    loads_signed: num(r.loads_signed),
    bol_count: num(r.bol_count),
  };
}

type Skin = { label: string; fill: string; rail: string };

/**
 * Exhaustive over the `move_job_status` enum (planned, active, complete). An
 * unknown value is a slate dash: it used to fall back to Planned.
 */
const STATUS: Record<string, Skin> = {
  planned: { label: "Planned", fill: "bg-fi-signal-amber", rail: "border-l-fi-amber" },
  active: { label: "Active", fill: "bg-fi-signal-blue", rail: "border-l-fi-blue" },
  complete: { label: "Complete", fill: "bg-fi-signal-green", rail: "border-l-fi-green" },
};
const UNKNOWN: Skin = { label: "—", fill: "bg-fi-signal-slate", rail: "border-l-transparent" };
const skinOf = (s: string | null) => (s != null && Object.prototype.hasOwnProperty.call(STATUS, s) ? STATUS[s] : UNKNOWN);

function StatusBadge({ status }: { status: string | null }) {
  const skin = skinOf(status);
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

type StatusFilter = "all" | "planned" | "active" | "complete";
const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "planned", label: "Planned" },
  { key: "active", label: "Active" },
  { key: "complete", label: "Complete" },
];

/** "4/9 signed" from the master BOL, or a dash with no master BOL to count from. */
function Progress({ job }: { job: MoveJobRow }) {
  if (job.total_loads == null || job.loads_signed == null) {
    return <span className="text-xs text-fieldiq-muted">No master BOL</span>;
  }
  const pct = job.total_loads > 0 ? Math.min(100, (job.loads_signed / job.total_loads) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-fieldiq-border" aria-hidden="true">
        <div className="h-full rounded-full bg-fi-signal-green" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-xs text-fieldiq-text">
        {job.loads_signed}/{job.total_loads} signed
      </span>
    </div>
  );
}

const fmtDay = (d: string | null) =>
  formatCalendarDate(d, { year: "numeric", month: "2-digit", day: "2-digit" }, "en-CA") ?? "—";

const MoveJobsScreen = () => {
  const navigate = useNavigate();
  const moveJobsQuery = useMoveJobs();
  const failed = queryLoadFailure(moveJobsQuery) != null;
  const loading = moveJobsQuery.isLoading && !failed;
  const jobs = useMemo(
    () => (moveJobsQuery.data == null ? undefined : unwrapArray<Record<string, unknown>>(moveJobsQuery.data).map(toRow)),
    [moveJobsQuery.data],
  );
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [showNewJob, setShowNewJob] = useState(false);

  const filtered = (jobs ?? []).filter((j) => {
    if (filter !== "all" && j.status !== filter) return false;
    if (search) {
      // Null-safe: `origin_lsd` is nullable, and `.toLowerCase()` on it threw.
      const q = search.toLowerCase();
      return [j.name, j.operator_name, j.origin_lsd, j.destination_lsd].some((v) => (v ?? "").toLowerCase().includes(q));
    }
    return true;
  });

  /** Tallies of the loaded list: GET /move-jobs is unpaginated and no endpoint returns these. */
  const countOf = (k: StatusFilter): number | null => (jobs ? jobs.filter((j) => k === "all" || j.status === k).length : null);
  const tally = (k: StatusFilter) => (failed ? "—" : displayValue(countOf(k)));

  const open = (id: string) => navigate(`/trucking/move-jobs/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      open(id);
    }
  };

  return (
    <div data-testid="move-jobs-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Trucking"
        title="Move Jobs"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <button type="button" onClick={() => setShowNewJob(true)} className="fi-action fi-action--standard fi-action--primary">
            <Plus className="size-4" aria-hidden="true" />
            New Move Job
          </button>
        }
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: "Move Jobs", sub: "On record", tone: "neutral", loading, value: tally("all") },
          { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: "Planned", sub: "Not started", tone: "accent", loading, value: tally("planned") },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: "Active", sub: "Moving now", tone: "info", loading, value: tally("active") },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: "Complete", sub: "Finished", tone: "success", loading, value: tally("complete") },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
              {FILTERS.map((f) => {
                const active = filter === f.key;
                const n = countOf(f.key);
                return (
                  <button
                    key={f.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setFilter(f.key)}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active
                        ? "bg-fi-amber text-fi-amber-ink"
                        : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
                    }`}
                  >
                    {f.label}
                    {!loading && !failed && n != null && (
                      <span className={`tabular-nums ${active ? "text-fi-amber-ink" : "text-fieldiq-muted"}`}>{n}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="relative lg:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                placeholder="Search job name, operator, LSD..."
                aria-label="Search move jobs"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-10 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </div>
          </div>

          <section
            aria-labelledby="move-job-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Move job register</p>
              <h2 id="move-job-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                Rig moves
              </h2>
            </header>

            {failed ? (
              <LoadFailureNotice error={queryFailureError(moveJobsQuery)} subject="move jobs" />
            ) : loading ? (
              <div className="space-y-2 p-4" aria-hidden="true">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">
                {search || filter !== "all" ? "No move jobs match these filters" : "No move jobs yet"}
              </p>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[900px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                      <tr className="border-l-4 border-l-transparent text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                        <th className="px-4 py-3">Job name</th>
                        <th className="px-4 py-3">Operator</th>
                        <th className="px-4 py-3">Route</th>
                        <th className="px-4 py-3">Planned start</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Loads linked</th>
                        <th className="px-4 py-3">Master BOL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((job) => (
                        <tr
                          key={job.id}
                          data-testid="move-job-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => open(job.id)}
                          onKeyDown={rowKeys(job.id)}
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${skinOf(job.status).rail}`}
                        >
                          <td className="px-4 py-3">
                            <p className="text-sm font-extrabold text-fieldiq-text">{job.name}</p>
                            {job.rig_number && <p className="text-[11px] text-fieldiq-muted">{job.rig_number}</p>}
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{job.operator_name ?? "—"}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 font-mono text-xs text-fieldiq-muted">
                              <span>{job.origin_lsd ?? "—"}</span>
                              <ArrowRight className="h-3 w-3" aria-hidden="true" />
                              <span>{job.destination_lsd ?? "—"}</span>
                            </div>
                          </td>
                          {/* `planned_start` is a CALENDAR DAY (an <input type="date"> writes it). */}
                          <td className="px-4 py-3 text-xs text-fieldiq-muted">{fmtDay(job.planned_start)}</td>
                          <td className="px-4 py-3"><StatusBadge status={job.status} /></td>
                          <td className="px-4 py-3 text-sm tabular-nums text-fieldiq-text">{displayValue(job.bol_count)}</td>
                          <td className="px-4 py-3"><Progress job={job} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {filtered.map((job) => (
                    <button
                      key={job.id}
                      type="button"
                      data-testid="move-job-card"
                      onClick={() => open(job.id)}
                      className="block w-full rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4 text-left"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-extrabold text-fieldiq-text">{job.name}</p>
                          <p className="text-xs text-fieldiq-muted">{job.operator_name ?? "—"}</p>
                        </div>
                        <StatusBadge status={job.status} />
                      </div>
                      <p className="mt-2 font-mono text-xs text-fieldiq-muted">
                        {job.origin_lsd ?? "—"} → {job.destination_lsd ?? "—"}
                      </p>
                      <div className="mt-2"><Progress job={job} /></div>
                    </button>
                  ))}
                </div>
                <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                  Showing {filtered.length} of {jobs?.length ?? 0} move jobs
                </p>
              </>
            )}
          </section>
        </div>
      </section>

      <NewMoveJobDialog open={showNewJob} onOpenChange={setShowNewJob} />
    </div>
  );
};

export default MoveJobsScreen;
